import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Award, Lock } from "lucide-react";
import api from "@/lib/api";

function levelColor(level) {
  if (level >= 9) return { t: "text-danger", b: "border-danger" };
  if (level >= 7) return { t: "text-grape", b: "border-grape" };
  if (level >= 5) return { t: "text-gold", b: "border-gold" };
  if (level >= 3) return { t: "text-neon", b: "border-neon" };
  return { t: "text-cream", b: "border-charcoal" };
}

export default function BadgeCard() {
  const { data } = useQuery({ queryKey: ["my-stats"], queryFn: async () => (await api.get("/me/stats")).data });
  const { data: cfg } = useQuery({ queryKey: ["community-config"], queryFn: async () => (await api.get("/community/config")).data });
  if (!data) return null;

  const level = data.level || 1;
  const color = levelColor(level);
  const pct = data.nextMin != null
    ? Math.max(4, Math.min(100, ((data.rep - data.curMin) / (data.nextMin - data.curMin)) * 100))
    : 100;
  const st = data.stats || {};
  const earned = new Set(data.badges || []);
  const catalog = cfg?.badges || [];

  return (
    <div className={`comic-card p-6 border-3 ${color.b}`} data-testid="rep-card">
      <div className="flex items-center gap-3">
        <Award className={`w-8 h-8 ${color.t}`} />
        <div>
          <p className="font-mono text-[10px] uppercase text-dim">ROBBED REP · Community Reputation</p>
          <p className={`font-display font-900 uppercase text-xl ${color.t}`} data-testid="rob-level">
            ROB LEVEL {level} · {data.levelName}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between font-mono text-[10px] text-dim">
          <span data-testid="rep-score">{data.rep} REP</span>
          {data.nextLevel ? <span>{data.repToNext} to L{data.nextLevel} · {data.nextLevelName}</span> : <span>Max level 👑</span>}
        </div>
        <div className="h-3 border-3 border-black bg-ink mt-1 overflow-hidden">
          <div className="h-full bg-neon transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        {[["Memes", st.memesTotal], ["Votes", st.votesReceived], ["Finalists", st.finalists],
          ["Wins", st.wins], ["Favorites", st.favorites], ["Stories", st.stories]].map(([l, v]) => (
          <div key={l} className="border-3 border-black bg-ink p-2">
            <p className="font-display font-900 text-neon text-lg">{v ?? 0}</p>
            <p className="font-mono text-[9px] uppercase text-dim">{l}</p>
          </div>
        ))}
      </div>

      {catalog.length > 0 && (
        <div className="mt-5">
          <p className="font-mono text-[10px] uppercase text-dim mb-2">Badges</p>
          <div className="flex flex-wrap gap-2" data-testid="my-badges">
            {catalog.map((b) => {
              const has = earned.has(b.id);
              return (
                <span key={b.id} title={b.desc}
                  className={`inline-flex items-center gap-1 border-2 px-2 py-1 font-heading font-bold uppercase text-[10px] leading-none ${has ? "border-neon text-neon bg-neon/10" : "border-charcoal text-dim opacity-50"}`}
                  data-testid={`badge-${b.id}${has ? "-earned" : "-locked"}`}>
                  {has ? b.icon : <Lock className="w-3 h-3" />} {b.name}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <p className="font-mono text-[10px] text-dim mt-4">
        REP is off-chain community reputation only — it is not $ROBBED, not a balance, and has no monetary value.
      </p>
    </div>
  );
}
